class JapanimeHeader extends HTMLElement {
  connectedCallback() {
    this.scrollThreshold = 7;
    this.lastScrollY = Math.max(window.scrollY, 0);
    this.scrollTicking = false;

    this.updateHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    };
    this.updateHeight();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.updateHeight);
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.updateHeight);
    }

    this.setupLanguageFlags();
    this.setupSearchGuard();
    if (this.classList.contains('jp-header-shell--sticky')) {
      this.classList.add('jp-header-shell--visible');
      this.setupSmartStickyHeader();
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.updateHeight);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('pageshow', this.onPageShow);
    this.removeEventListener('focusin', this.revealHeader);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('submit', this.onSearchSubmit, true);
    this.removeEventListener('input', this.onSearchInput, true);
    this.removeEventListener('focusin', this.onSearchFocus, true);
    this.removeEventListener('keydown', this.onSearchKeydown, true);
    this.removeEventListener('click', this.onLanguageSelection, true);
    if (this.onFiltersUpdateStart) {
      document.removeEventListener('japanime:filters-update-start', this.onFiltersUpdateStart);
    }
    if (this.onFiltersUpdateEnd) document.removeEventListener('japanime:filters-update-end', this.onFiltersUpdateEnd);
    this.drawerDetails?.forEach((details) => details.removeEventListener('toggle', this.onInteractionToggle));
    this.interactionObserver?.disconnect();
  }

  setupLanguageFlags() {
    this.onLanguageSelection = (event) => {
      const button = event.target.closest?.('.jp-language-flags__button[data-jp-language-code]');
      if (!button || !this.contains(button)) return;

      const form = button.form;
      const languageInput = form?.querySelector('[data-jp-language-input]');
      if (!form || !languageInput) return;

      event.preventDefault();
      languageInput.value = button.dataset.jpLanguageCode;
      languageInput.disabled = false;
      button.removeAttribute('name');

      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else HTMLFormElement.prototype.submit.call(form);
    };

    this.addEventListener('click', this.onLanguageSelection, true);
  }

  setupSearchGuard() {
    this.forceCloseSearch = (search, blurInput = false, clearResults = false) => {
      if (!search) return;

      search.classList.add('jp-search--force-closed');
      search.abortController?.abort();
      if (search.abortController) search.abortController = new AbortController();
      search.removeAttribute('open');
      search.removeAttribute('loading');
      if (clearResults) search.removeAttribute('results');
      search.isOpen = false;

      const input = search.querySelector('input[type="search"]');
      input?.setAttribute('aria-expanded', 'false');
      input?.setAttribute('aria-activedescendant', '');
      if (blurInput && input === document.activeElement) input.blur();

      const results = search.querySelector('[data-predictive-search]');
      if (results) {
        results.hidden = true;
        if (clearResults) results.replaceChildren();
      }
      results?.removeAttribute('style');
    };

    this.forceCloseAllSearches = (blurInput = false) => {
      this.querySelectorAll('predictive-search').forEach((search) => this.forceCloseSearch(search, blurInput));
    };

    this.onSearchSubmit = (event) => {
      const search = event.target.closest?.('predictive-search');
      if (search && this.contains(search)) {
        search.dataset.jpSearchSubmitted = 'true';
        this.forceCloseSearch(search, true, true);
      }
    };

    this.onSearchInput = (event) => {
      if (!event.target.matches?.('input[type="search"]')) return;
      const search = event.target.closest('predictive-search');
      if (!search || !this.contains(search)) return;

      if (event.target.value.trim()) search.classList.remove('jp-search--force-closed');
      else this.forceCloseSearch(search, false, true);
    };

    this.onSearchFocus = (event) => {
      if (!event.target.matches?.('input[type="search"]')) return;
      const search = event.target.closest('predictive-search');
      if (!search || !this.contains(search)) return;

      // A submitted term is rendered back into the header on the results page.
      // Focus alone must never reopen the old predictive panel: only a real
      // input event is allowed to opt the component back in.
      if (search.dataset.jpSearchSubmitted === 'true') this.forceCloseSearch(search, false, true);
    };

    this.onSearchKeydown = (event) => {
      if (event.key !== 'Enter' || !event.target.matches?.('input[type="search"]')) return;
      const search = event.target.closest('predictive-search');
      if (!search || !this.contains(search)) return;

      const selectedSuggestion = search.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selectedSuggestion || !event.target.value.trim()) return;

      event.preventDefault();
      search.dataset.jpSearchSubmitted = 'true';
      this.forceCloseSearch(search, false, true);
      if (event.target.form?.requestSubmit) event.target.form.requestSubmit();
      else event.target.form?.submit();
    };

    this.onPageShow = () => {
      this.querySelectorAll('predictive-search').forEach((search) => {
        if (search.querySelector('input[type="search"]')?.value.trim()) search.dataset.jpSearchSubmitted = 'true';
        this.forceCloseSearch(search, false, true);
      });
    };

    this.addEventListener('submit', this.onSearchSubmit, true);
    this.addEventListener('input', this.onSearchInput, true);
    this.addEventListener('focusin', this.onSearchFocus, true);
    this.addEventListener('keydown', this.onSearchKeydown, true);
    window.addEventListener('pageshow', this.onPageShow);
    this.querySelectorAll('predictive-search').forEach((search) => {
      if (search.querySelector('input[type="search"]')?.value.trim()) search.dataset.jpSearchSubmitted = 'true';
      this.forceCloseSearch(search, false, true);
    });
  }

  setupSmartStickyHeader() {
    this.filterRevealHoldUntil = 0;

    this.isCompactProductHeader = () => {
      return document.body.classList.contains('jp-product-page') && window.matchMedia('(max-width: 900px)').matches;
    };

    this.revealHeader = () => {
      this.classList.remove('jp-header-shell--hidden');
      this.classList.add('jp-header-shell--visible');
    };

    this.hasActiveInteraction = () => {
      const activeElement = document.activeElement;
      const focusedSearch = activeElement && this.contains(activeElement)
        ? activeElement.closest?.('predictive-search, [role="search"], .jp-header__search')
        : null;
      return Boolean(this.querySelector('details[open]') || focusedSearch);
    };

    this.hideHeader = () => {
      if (this.hasActiveInteraction()) return;
      this.classList.remove('jp-header-shell--visible');
      this.classList.add('jp-header-shell--hidden');
    };

    this.onScroll = () => {
      if (this.scrollTicking) return;
      this.scrollTicking = true;

      window.requestAnimationFrame(() => {
        const currentScrollY = Math.max(window.scrollY, 0);
        const delta = currentScrollY - this.lastScrollY;
        const compactProductHeader = this.isCompactProductHeader();
        const threshold = compactProductHeader ? 1 : this.scrollThreshold;
        const hideBoundary = compactProductHeader ? 12 : this.offsetHeight;

        const activeInteraction = this.hasActiveInteraction();
        const suppressFilterReveal = performance.now() < this.filterRevealHoldUntil;

        if (delta > threshold && currentScrollY > hideBoundary && !activeInteraction) {
          this.forceCloseAllSearches(true);
        }

        if (currentScrollY <= 12 || activeInteraction) {
          this.revealHeader();
        } else if (delta > threshold && currentScrollY > hideBoundary) {
          this.hideHeader();
        } else if (delta < -threshold && !suppressFilterReveal) {
          this.revealHeader();
        }

        this.lastScrollY = currentScrollY;
        this.scrollTicking = false;
      });
    };

    this.onInteractionToggle = () => {
      if (this.hasActiveInteraction()) this.revealHeader();
    };

    this.onFocusOut = () => {
      window.requestAnimationFrame(() => {
        if (this.hasActiveInteraction()) this.revealHeader();
      });
    };

    this.onFiltersUpdateStart = () => {
      this.filterRevealHoldUntil = Math.max(this.filterRevealHoldUntil, performance.now() + 900);
    };

    this.onFiltersUpdateEnd = () => {
      this.filterRevealHoldUntil = Math.max(this.filterRevealHoldUntil, performance.now() + 550);
    };

    this.drawerDetails = this.querySelectorAll('details');
    this.drawerDetails.forEach((details) => details.addEventListener('toggle', this.onInteractionToggle));
    this.addEventListener('focusin', this.revealHeader);
    this.addEventListener('focusout', this.onFocusOut);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('japanime:filters-update-start', this.onFiltersUpdateStart);
    document.addEventListener('japanime:filters-update-end', this.onFiltersUpdateEnd);

    this.interactionObserver = new MutationObserver(() => {
      if (this.hasActiveInteraction()) this.revealHeader();
    });
    this.interactionObserver.observe(this, {
      attributes: true,
      subtree: true,
      attributeFilter: ['open', 'loading', 'aria-expanded'],
    });
  }
}

if (!customElements.get('japanime-header')) {
  customElements.define('japanime-header', JapanimeHeader);
}
